import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify/unstyled';

import { useAppDispatch } from '@app/store/store';
import { setCredentials } from '@features/authentication/store/authentication.slice';
import type { Tenant } from '@features/tenants/api/tenants.types';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@shared/components/ui';
import { GenericButton, GenericModal, GenericSelect } from '@shared/components/ui/custom';
import { resetAllApiState } from '@shared/utils';

import { useLoginMutation } from '../../api/authentication.api';

import { type SelectTenantFormValues, SelectTenantSchema } from './schemas/select-tenant.schema';

interface SelectTenantModalProps {
  onClose: () => void;
  tenants: Tenant[];
  username: string;
  password: string;
  onTermsRequired: (data: { tenantId: string; token: string; email: string }) => void;
}

export const SelectTenantModal = ({
  onClose,
  tenants,
  username,
  password,
  onTermsRequired,
}: SelectTenantModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [login, { isLoading, reset }] = useLoginMutation();

  const form = useForm<SelectTenantFormValues>({
    resolver: zodResolver(SelectTenantSchema),
    defaultValues: {
      tenantId: '',
    },
  });

  const handleClose = () => {
    onClose();
    reset?.();
  };

  const onSubmit = async (values: SelectTenantFormValues) => {
    try {
      const data = await login({ email: username, password, tenant: values.tenantId }).unwrap();

      if (!data) {
        toast.error('Authentication error');
        return;
      }

      const { message, token, tenants: returnedTenants } = data;

      if (message === 'Authentication successful') {
        const selected = returnedTenants.find((t) => t.tenantId === values.tenantId);

        dispatch(resetAllApiState());
        dispatch(
          setCredentials({
            username,
            token,
            ...(selected?.tenantId && { tenantId: selected.tenantId }),
            ...(selected?.tenantName && { tenantName: selected.tenantName }),
          }),
        );

        void navigate('/');
        toast.success(`Logged into ${selected?.tenantName ?? values.tenantId}`);
        handleClose();
        return;
      }

      if (message === 'Tenant has not accepted the terms and conditions') {
        handleClose();
        onTermsRequired({
          tenantId: returnedTenants[0]?.tenantId ?? '',
          token,
          email: username,
        });
        return;
      }
    } catch (error) {
      console.error('Authentication error:', error);
      toast.error('Authentication error');
    }
  };

  return (
    <GenericModal
      open
      onOpenChange={handleClose}
      title="Select Tenant"
      description="Select a tenant to log in with for this session."
      data-testid="authentication-select-tenant-modal"
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 pt-2"
          noValidate
          data-testid="authentication-select-tenant-content"
        >
          <FormField
            control={form.control}
            name="tenantId"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <GenericSelect
                    options={
                      tenants?.map((tenant) => ({
                        value: tenant.tenantId,
                        label: tenant.tenantName,
                      })) ?? []
                    }
                    placeholder="Select a tenant"
                    value={field.value}
                    onChange={field.onChange}
                    data-testid="authentication-select-tenant-select"
                  />
                </FormControl>
                <FormMessage data-testid="authentication-select-tenant-error" />
              </FormItem>
            )}
          />

          <GenericButton
            label="Log in"
            type="submit"
            className="w-full"
            isLoading={isLoading}
            data-testid="authentication-select-tenant-login-button"
          />
        </form>
      </Form>
    </GenericModal>
  );
};
